import React, { useState, useEffect } from "react";
import { API } from "@/App";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";

const STORIES = [
  { quote: "I used to think managing my diet meant giving up the Nigerian foods I enjoy. My dietitian helped me understand portions and make better choices without completely changing how I eat. The advice was practical and easy to follow.", name: "Amaka O.", rating: 5 },
  { quote: "What I liked most was that my meal plan considered my budget, my schedule and the foods I actually eat. It wasn't one of those plans that looks good on paper but is difficult to follow in real life.", name: "Chidinma E.", rating: 5 },
  { quote: "Having someone check in on my progress made a difference. When I struggled to stick to the plan, we talked about what was getting in the way and made adjustments. I felt supported rather than judged.", name: "Tunde A.", rating: 5 },
  { quote: "I appreciated that my nutrition needs were considered alongside the care I was already receiving from my doctor. The explanations were clear, and I had a better understanding of how food choices fit into my overall health plan.", name: "Ngozi I.", rating: 5 },
];

export const PatientStories = ({ limit, bg = "bg-white" }) => {
  const [extra, setExtra] = useState([]);
  useEffect(() => {
    if (limit) return;
    fetch(`${API}/public/stories`).then(r => r.ok ? r.json() : { stories: [] }).then(d => setExtra((d.stories || []).filter(x => (x.rating || 0) >= 3))).catch(() => {});
  }, [limit]);
  const all = limit ? STORIES.slice(0, limit) : [...STORIES, ...extra];
  return (
  <section id="stories" className={`${bg} scroll-mt-16`} data-testid={limit ? "home-patient-stories" : "patient-stories"}>
    <div className={`max-w-5xl mx-auto px-5 ${limit ? "pt-12 pb-8 sm:pt-14 sm:pb-10" : "py-14"}`}>
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="inline-block text-xs font-semibold tracking-[0.15em] uppercase text-[#FF7A00] mb-3">Patient Stories</span>
        <h2 className="text-2xl sm:text-3xl font-bold text-stone-900" data-testid="patient-stories-heading">Better nutrition starts with the right support.</h2>
        <p className="mt-3 text-sm sm:text-base text-stone-500">Personalised guidance. Practical changes. Support that fits real life.</p>
      </div>
      <div className="relative">
      <div className={`grid sm:grid-cols-2 gap-5 ${!limit && all.length > 4 ? "max-h-[640px] overflow-y-auto pr-2 pb-1 [scrollbar-width:thin]" : ""}`} data-testid="stories-scroll">
        {all.map((s, i) => (
          <figure key={`${s.name}-${i}`} className="relative rounded-2xl border border-stone-100 bg-[#FAFAF9] p-6 sm:p-7 hover:border-[#FF7A00]/20 transition-colors" data-testid={`story-card-${i + 1}`}>
            <span className="absolute top-3 right-5 text-5xl leading-none font-serif text-[#FF7A00]/20 select-none" aria-hidden="true">&rdquo;</span>
            <div className="flex gap-0.5 mb-3" aria-label={`${s.rating || 5} out of 5 stars`} data-testid={`story-stars-${i + 1}`}>
              {[1, 2, 3, 4, 5].map(n => <Star key={n} className={`w-4 h-4 ${n <= (s.rating || 5) ? "fill-[#FF7A00] text-[#FF7A00]" : "text-stone-200"}`} />)}
            </div>
            <blockquote className="text-sm sm:text-[15px] text-stone-700 leading-relaxed">&ldquo;{s.quote}&rdquo;</blockquote>
            <figcaption className="mt-5 flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-[#FF7A00]/10 text-[#FF7A00] text-sm font-bold flex items-center justify-center">{s.name[0]}</span>
              <span className="text-sm font-semibold text-stone-900">{s.name}</span>
            </figcaption>
          </figure>
        ))}
      </div>
      {limit && (
        <div className="mt-8 text-center">
          <Link to="/programme#stories" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF7A00] hover:text-[#E56E00] transition-colors group" data-testid="see-more-stories">
            See more patient stories <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      )}
      {!limit && all.length > 4 && <div className="pointer-events-none absolute bottom-0 left-0 right-2 h-14 bg-gradient-to-t from-white to-transparent" />}
      </div>
      {!limit && all.length > 4 && <p className="mt-3 text-center text-xs text-stone-400" data-testid="stories-scroll-hint">Scroll to read more stories</p>}
    </div>
  </section>
  );
};

